import { Component, Optional, ViewChild } from '@angular/core';
import { _HttpClient } from '@delon/theme';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxTreeListComponent } from 'devextreme-angular/ui/tree-list';
import { Column } from 'devextreme/ui/data_grid';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

import { ReportNavService } from '../../../core/util/report-nav.service';
import { LayoutManagementComponent } from '../modal/layout-management/layout-management.component';
import { SaveLayoutComponent } from '../modal/layout-management/save-layout.component';

export interface GridLayout {
  id: number;
  componentName: string;
  layoutName: string;
  layout: string;
  isDefault: boolean;
  userId?: string;
  createdDate?: Date;
  modifiedDate?: Date;
}

@Component({
  template: ''
})
export abstract class LayoutManagementBase {
  @ViewChild('gridContainer', { static: false })
  gridComponent!: DxDataGridComponent;

  savedLayouts: GridLayout[] = [];

  originalColumns: Column[] = [];
  selectedLayout: string = '';

  columnChooserConfigs = {
    height: 450
  };

  constructor(
    protected http: _HttpClient,
    protected msg: NzMessageService,
    protected modal: NzModalService,
    @Optional() protected nav?: ReportNavService
  ) {}

  // Abstract method to be implemented by child classes
  abstract getComponentName(): string;

  loadSavedLayouts(
    applyDefaultLayout: boolean = true,
    dxGrid?: DxDataGridComponent
  ): void {
    this.http.get(`api/gridlayout/${this.getComponentName()}`).subscribe({
      next: (res: any) => {
        if (res.length > 0) {
          this.savedLayouts = res;
          // Apply default layout if exists
          const defaultLayout = this.savedLayouts.find(l => l.isDefault);
          if (defaultLayout && applyDefaultLayout) {
            this.applyLayout(defaultLayout.layoutName, dxGrid);
          }
        }
      },
      error: err => {
        this.msg.error('Failed to load layouts');
        console.error('Error loading layouts:', err);
      }
    });
  }

  saveCurrentLayout(
    dxGrid?: DxDataGridComponent | DxTreeListComponent,
    columns?: any
  ): void {
    const grid = dxGrid || this.gridComponent;
    if (!grid || !grid.instance) return;

    const currentLayout = grid.instance.state();
    const defaultColumns = columns || this.originalColumns;

    // Explicitly handle the checkbox column
    const checkboxColumn = defaultColumns.find(
      (col: any) =>
        col.cellTemplate === 'dxCheckboxTpl' &&
        col.headerCellTemplate === 'dxHeaderCheckboxTpl'
    );

    // Merge original column properties
    currentLayout.columns = currentLayout.columns.map((col: any) => {
      const originalCol = defaultColumns.find(
        (origCol: any) => origCol.dataField === col.dataField
      );
      if (originalCol) {
        col = {
          ...col,
          format: originalCol.format,
          caption: originalCol.caption,
          cellTemplate: originalCol.cellTemplate,
          headerCellTemplate: originalCol.headerCellTemplate,
          calculateCellValue: originalCol.calculateCellValue,
          headerFilter: originalCol.headerFilter,
          lookup: originalCol.lookup,
          allowFiltering: originalCol.allowFiltering,
          allowHiding: originalCol.allowHiding,
          allowSearch: originalCol.allowSearch,
          allowHeaderFiltering: originalCol.allowHeaderFiltering,
          allowResizing: originalCol.allowResizing,
          allowReordering: originalCol.allowReordering,
          allowSorting: originalCol.allowSorting,
          allowExporting: originalCol.allowExporting,
          fixed: originalCol.fixed,
          width: originalCol.width
        };
      }
      return col;
    });

    // Ensure checkbox column is included with correct properties
    if (checkboxColumn) {
      const checkboxColIndex = currentLayout.columns.findIndex(
        (col: any) => col.dataField === checkboxColumn.dataField
      );
      if (checkboxColIndex >= 0) {
        // Update existing checkbox column
        currentLayout.columns[checkboxColIndex] = {
          ...currentLayout.columns[checkboxColIndex],
          ...checkboxColumn
        };
      } else {
        // Add checkbox column if missing
        currentLayout.columns.unshift(checkboxColumn);
      }
    }

    const modalRef = this.modal.create({
      nzContent: SaveLayoutComponent,
      nzTitle: 'Save Layout',
      nzFooter: null,
      nzWidth: 400,
      nzCentered: true,
      nzClosable: true,
      nzMaskClosable: false
    });

    modalRef.afterClose.subscribe(result => {
      if (result) {
        const newLayout = {
          componentName: this.getComponentName(),
          layoutName: result.name,
          layout: JSON.stringify(currentLayout),
          isDefault: result.isDefault
        };

        this.http.post('api/gridlayout', newLayout).subscribe({
          next: (res: any) => {
            if (res.id > 0) {
              this.loadSavedLayouts(false);
            }
          },
          error: err => {
            this.msg.error('Failed to save layout');
            console.error('Error saving layout:', err);
          }
        });
      }
    });
  }
  applyDefaultLayout(dxGrid?: any): void {
    const defaultLayout = this.savedLayouts.find(l => l.isDefault);
    if (defaultLayout) {
      this.applyLayout(defaultLayout.layoutName, dxGrid);
    } else {
      console.warn('No default layout found');
    }
  }

  applyLayout(layoutName: string, dxGrid?: any): void {
    const layout = this.savedLayouts.find(l => l.layoutName === layoutName);
    let grid = dxGrid || this.gridComponent;
    if (!layout || !grid || !grid.instance) return;

    try {
      const layoutState =
        typeof layout.layout === 'string'
          ? JSON.parse(layout.layout)
          : layout.layout;

      // Apply layout settings
      grid.instance.option('columns', layoutState.columns);
      grid.instance.option('grouping.mode', layoutState.grouping);

      grid.instance.refresh();
      this.selectedLayout = layoutName;
    } catch (err) {
      this.msg.error('Failed to apply layout');
      console.error('Error applying layout:', err);
    }
  }

  deleteLayout(layoutName: string): void {
    const layout = this.savedLayouts.find(l => l.layoutName === layoutName);
    if (!layout) return;

    this.http.delete(`api/gridlayout/${layout.id}`).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.loadSavedLayouts(false);
          if (this.selectedLayout === layoutName) {
            this.selectedLayout = '';
          }
        }
      },
      error: err => {
        this.msg.error('Failed to delete layout');
        console.error('Error deleting layout:', err);
      }
    });
  }

  resetGrid(dxGrid?: DxDataGridComponent, columns?: any): void {
    let grid = dxGrid || this.gridComponent;

    if (grid && grid.instance) {
      grid.instance.clearFilter();
      grid.instance.clearGrouping();
      grid.instance.option('columns', this.getResetColumns(columns));
      grid.instance.clearSorting();

      grid.instance.refresh();
    }
  }

  protected getResetColumns(columns?: any): any[] {
    return columns || this.originalColumns;
  }

  resetTree(dxTree: DxTreeListComponent, columns?: any) {
    dxTree.instance.clearFilter();
    dxTree.instance.clearSorting();
    dxTree.instance.option('columns', columns || this.originalColumns);
  }

  openLayoutManagementPopup(): void {
    const modalRef = this.modal.create({
      nzContent: LayoutManagementComponent,
      nzFooter: null,
      nzWidth: 800,
      nzData: {
        layouts: this.savedLayouts
      }
    });

    modalRef.afterClose.subscribe(result => {
      if (result) {
        // Reload layouts to get the latest changes
        this.loadSavedLayouts(false);
      }
    });
  }

  thousandSeparator(e: any) {
    if (e.value === undefined || e.value === null) return '';
    let formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return formatter.format(e.value);
  }
}
